<template>
  <v-main>
    <v-container>
      <v-card>
        <v-card-title class="headline"> 🧪 Billing System Test </v-card-title>

        <v-card-text>
          <!-- Connection Status -->
          <v-row>
            <v-col cols="12">
              <v-alert
                :type="connectionStatus === 'connected' ? 'success' : 'warning'"
                :icon="
                  connectionStatus === 'connected' ? 'mdi-wifi' : 'mdi-wifi-off'
                "
              >
                WebSocket Status: {{ connectionStatus.toUpperCase() }}
                <br />
                <small>Host: {{ wsHost }}:{{ wsPort }}</small>
              </v-alert>
            </v-col>
          </v-row>

          <!-- Test Controls -->
          <v-row>
            <v-col cols="12" md="6">
              <v-select
                v-model="selectedConsultation"
                :items="consultations"
                item-text="title"
                item-value="id"
                label="Select Consultation"
                @change="loadConsultationDetails"
              >
                <template v-slot:selection="{ item }">
                  {{ item.title }} (ID: {{ item.id }})
                </template>
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title>{{ item.title }}</v-list-item-title>
                    <v-list-item-subtitle
                      >ID: {{ item.id }} | Status:
                      {{ getStatusText(item.status) }} | Doctor:
                      {{ item.doctor?.user_name }} | Patient:
                      {{ item.patient?.user_name }}</v-list-item-subtitle
                    >
                  </v-list-item-content>
                </template>
              </v-select>
            </v-col>
            <v-col cols="12" md="6">
              <v-btn
                color="primary"
                @click="startBillingTest"
                :disabled="!selectedConsultation || testInProgress"
                :loading="testInProgress"
              >
                Start Billing Test
              </v-btn>
              <v-btn
                color="warning"
                @click="stopBillingTest"
                :disabled="!selectedConsultation"
                class="ml-2"
              >
                Stop Billing
              </v-btn>
            </v-col>
          </v-row>

          <!-- Consultation Details -->
          <v-row v-if="consultationDetails">
            <v-col cols="12">
              <v-card outlined>
                <v-card-subtitle>Consultation Details</v-card-subtitle>
                <v-card-text>
                  <v-row>
                    <v-col cols="6">
                      <strong>ID:</strong> {{ consultationDetails.id }}<br />
                      <strong>Status:</strong>
                      {{ getStatusText(consultationDetails.status) }}<br />
                      <strong>Doctor:</strong>
                      {{ consultationDetails.doctor?.user_name }}<br />
                    </v-col>
                    <v-col cols="6">
                      <strong>Patient:</strong>
                      {{ consultationDetails.patient?.user_name }}<br />
                      <strong>Rate:</strong> ₹{{
                        consultationDetails.doctor
                          ?.consultation_rate_per_minute
                      }}/min<br />
                      <strong>Patient Wallet:</strong> ₹{{
                        consultationDetails.patient?.wallet
                      }}
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- Real-time Events Log -->
          <v-row>
            <v-col cols="12">
              <v-card outlined>
                <v-card-subtitle
                  class="d-flex justify-space-between align-center"
                >
                  Real-time Events
                  <v-btn small @click="clearEvents">Clear</v-btn>
                </v-card-subtitle>
                <v-card-text>
                  <div
                    class="events-log"
                    style="
                      height: 300px;
                      overflow-y: auto;
                      background: #f5f5f5;
                      padding: 10px;
                      font-family: monospace;
                    "
                  >
                    <div
                      v-for="(event, index) in events"
                      :key="index"
                      :class="getEventClass(event.type)"
                      style="
                        margin-bottom: 5px;
                        padding: 5px;
                        border-radius: 3px;
                      "
                    >
                      <strong>[{{ formatTime(event.timestamp) }}]</strong>
                      {{ event.type }}: {{ event.message }}
                      <div
                        v-if="event.data"
                        class="mt-1"
                        style="font-size: 0.9em"
                      >
                        {{ JSON.stringify(event.data, null, 2) }}
                      </div>
                    </div>
                    <div
                      v-if="events.length === 0"
                      class="text-center text-muted"
                    >
                      No events received yet. Start a billing test to see
                      real-time events.
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- Billing Records -->
          <v-row v-if="billingRecords.length > 0">
            <v-col cols="12">
              <v-card outlined>
                <v-card-subtitle>Current Billing Records</v-card-subtitle>
                <v-simple-table>
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Status</th>
                        <th>Minutes</th>
                        <th>Amount</th>
                        <th>Rate</th>
                        <th>Started</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="record in billingRecords" :key="record.id">
                        <td>{{ record.id }}</td>
                        <td>
                          <v-chip
                            :color="
                              record.status === 'active' ? 'green' : 'grey'
                            "
                            text-color="white"
                            small
                          >
                            {{ record.status }}
                          </v-chip>
                        </td>
                        <td>{{ record.total_minutes_billed }}</td>
                        <td>₹{{ record.total_amount_deducted }}</td>
                        <td>₹{{ record.rate_per_minute }}/min</td>
                        <td>{{ formatDateTime(record.started_at) }}</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";
import websocketService from "@/services/websocket";

export default {
  name: "BillingSystemTest",
  data() {
    return {
      connectionStatus: "disconnected",
      wsHost: "127.0.0.1",
      wsPort: 6001,
      consultations: [],
      selectedConsultation: null,
      consultationDetails: null,
      testInProgress: false,
      events: [],
      billingRecords: [],
      refreshInterval: null,
      billingChannels: [],
    };
  },

  async mounted() {
    this.logEvent("info", "Component mounted, initializing...");

    // Connect to WebSocket if not already connected
    if (!websocketService.isConnected()) {
      this.logEvent("info", "Connecting to WebSocket...");
      websocketService.connect();
    }

    // Monitor connection status
    this.checkConnectionStatus();
    setInterval(this.checkConnectionStatus, 2000);

    // Load consultations
    await this.loadConsultations();

    // Set up WebSocket event listeners
    this.setupWebSocketListeners();
  },

  beforeDestroy() {
    // Clean up
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }

    // Leave all billing channels
    this.billingChannels.forEach((channelName) => {
      websocketService.echo?.leave(channelName);
    });
  },

  methods: {
    checkConnectionStatus() {
      this.connectionStatus = websocketService.isConnected()
        ? "connected"
        : "disconnected";

      if (this.connectionStatus === "connected") {
        this.wsHost =
          websocketService.echo?.connector?.pusher?.config?.wsHost || "Unknown";
        this.wsPort =
          websocketService.echo?.connector?.pusher?.config?.wsPort || "Unknown";
      }
    },

    async loadConsultations() {
      try {
        this.logEvent("info", "Loading consultations...");
        const response = await axios.get("/consultations");
        this.consultations = response.data.data || [];
        this.logEvent(
          "success",
          `Loaded ${this.consultations.length} consultations`
        );
      } catch (error) {
        this.logEvent(
          "error",
          "Failed to load consultations: " + error.message
        );
      }
    },

    async loadConsultationDetails() {
      if (!this.selectedConsultation) return;

      try {
        this.logEvent(
          "info",
          `Loading details for consultation ${this.selectedConsultation}...`
        );
        const encryptedId = this.encryptIt(this.selectedConsultation);
        const response = await axios.get(`/consultations/${encryptedId}`);
        this.consultationDetails = response.data.data;

        // Load billing records for this consultation
        await this.loadBillingRecords();

        this.logEvent("success", "Consultation details loaded");
      } catch (error) {
        this.logEvent(
          "error",
          "Failed to load consultation details: " + error.message
        );
      }
    },

    async loadBillingRecords() {
      if (!this.selectedConsultation) return;

      try {
        const response = await axios.get(
          `/consultation_billings?consultation_id=${this.selectedConsultation}`
        );
        this.billingRecords = response.data.data || [];
        this.logEvent(
          "info",
          `Found ${this.billingRecords.length} billing records`
        );
      } catch (error) {
        this.logEvent(
          "error",
          "Failed to load billing records: " + error.message
        );
      }
    },

    setupWebSocketListeners() {
      if (!websocketService.echo) {
        this.logEvent(
          "warning",
          "WebSocket not connected, cannot set up listeners"
        );
        return;
      }

      // Listen for connection events
      websocketService.echo.connector.pusher.connection.bind(
        "connected",
        () => {
          this.logEvent("success", "WebSocket connected successfully!");
        }
      );

      websocketService.echo.connector.pusher.connection.bind(
        "disconnected",
        () => {
          this.logEvent("warning", "WebSocket disconnected");
        }
      );

      this.logEvent("info", "WebSocket event listeners set up");
    },

    async startBillingTest() {
      if (!this.selectedConsultation) return;

      this.testInProgress = true;
      this.logEvent(
        "info",
        `Starting billing test for consultation ${this.selectedConsultation}`
      );

      try {
        // Set up billing event listeners
        await this.setupBillingEventListeners();
        // Update consultation status to approved (1) to trigger billing
        const encryptedId = this.encryptIt(this.selectedConsultation);
        const response = await axios.patch(`/consultations/${encryptedId}`, {
          status: 1,
        });

        if (response.data.success) {
          this.logEvent(
            "success",
            "Consultation approved - billing should start automatically"
          );
          this.consultationDetails.status = 1;

          // Start refreshing billing records
          this.startBillingRecordRefresh();
        } else {
          this.logEvent("error", "Failed to approve consultation");
        }
      } catch (error) {
        this.logEvent("error", "Error starting billing test: " + error.message);
      } finally {
        this.testInProgress = false;
      }
    },

    async stopBillingTest() {
      if (!this.selectedConsultation) return;

      this.logEvent(
        "info",
        `Stopping billing test for consultation ${this.selectedConsultation}`
      );

      try {
        // Update consultation status to completed (2) to stop billing
        const encryptedId = this.encryptIt(this.selectedConsultation);
        const response = await axios.patch(`/consultations/${encryptedId}`, {
          status: 2,
        });

        if (response.data.success) {
          this.logEvent(
            "success",
            "Consultation completed - billing should stop"
          );
          this.consultationDetails.status = 2;

          // Stop refreshing
          if (this.refreshInterval) {
            clearInterval(this.refreshInterval);
            this.refreshInterval = null;
          }

          // Final refresh of billing records
          setTimeout(() => {
            this.loadBillingRecords();
          }, 2000);
        } else {
          this.logEvent("error", "Failed to complete consultation");
        }
      } catch (error) {
        this.logEvent("error", "Error stopping billing test: " + error.message);
      }
    },

    async setupBillingEventListeners() {
      if (!websocketService.echo || !this.consultationDetails) return;

      const consultationId = this.consultationDetails.id;
      const patientId = this.consultationDetails.patient_id;
      const doctorId = this.consultationDetails.doctor_id;

      this.logEvent(
        "info",
        `Setting up billing event listeners for consultation ${consultationId}`
      );

      try {
        // Listen on private billing channel for this consultation
        const billingChannel = `billing.${consultationId}`;
        const channel1 = websocketService.echo.private(billingChannel);
        this.billingChannels.push(billingChannel);

        // Listen on user private channels
        const patientChannel = `user.${patientId}`;
        const doctorChannel = `user.${doctorId}`;
        const channel2 = websocketService.echo.private(patientChannel);
        const channel3 = websocketService.echo.private(doctorChannel);
        this.billingChannels.push(patientChannel, doctorChannel);

        // Set up event listeners for billing events
        const events = [
          "billing.session.started",
          "billing.minute.processed",
          "billing.insufficient.funds", 
          "billing.session.stopped",
        ];

        events.forEach((eventName) => {
          // Listen on billing channel
          if (channel1) {
            channel1.listen(`.${eventName}`, (data) => {
              this.logEvent(
                "billing",
                `${eventName} received on billing channel`,
                data
              );
              this.handleBillingEvent(eventName, data);
            });
          }

          // Listen on patient channel
          if (channel2) {
            channel2.listen(`.${eventName}`, (data) => {
              this.logEvent(
                "billing",
                `${eventName} received on patient channel`,
                data
              );
              this.handleBillingEvent(eventName, data);
            });
          }

          // Listen on doctor channel
          if (channel3) {
            channel3.listen(`.${eventName}`, (data) => {
              this.logEvent(
                "billing",
                `${eventName} received on doctor channel`,
                data
              );
              this.handleBillingEvent(eventName, data);
            });
          }
        });

        this.logEvent(
          "success",
          `Billing event listeners set up for channels: ${this.billingChannels.join(
            ", "
          )}`
        );
      } catch (error) {
        this.logEvent(
          "error",
          "Error setting up billing event listeners: " + error.message
        );
      }
    },

    handleBillingEvent(eventName, data) {
      // Handle different billing events
      switch (eventName) {
        case 'billing.session.started':
          this.logEvent("success", "Billing session started", data);
          break;
        case 'billing.minute.processed':
          this.logEvent("info", `Minute billed: ₹${data.deducted_amount}`, data);
          // Update consultation details to show new balance
          if (this.consultationDetails && this.consultationDetails.patient) {
            this.consultationDetails.patient.wallet = data.remaining_balance;
          }
          break;
        case 'billing.insufficient.funds':
          this.logEvent("warning", "Insufficient funds detected", data);
          break;
        case 'billing.session.stopped':
          this.logEvent("info", `Billing stopped: ${data.reason}`, data);
          break;
      }

      // Always refresh billing records when any event occurs
      this.loadBillingRecords();
    },

    startBillingRecordRefresh() {
      // Refresh billing records every 10 seconds during active billing
      this.refreshInterval = setInterval(() => {
        this.loadBillingRecords();
        this.loadConsultationDetails(); // Refresh wallet balances
      }, 10000);
    },

    logEvent(type, message, data = null) {
      const event = {
        type,
        message,
        data,
        timestamp: new Date(),
      };

      this.events.unshift(event);

      // Keep only last 50 events
      if (this.events.length > 50) {
        this.events.splice(50);
      }

      // Also log to console
      console.log(`[${type.toUpperCase()}] ${message}`, data || "");
    },

    clearEvents() {
      this.events = [];
    },

    getEventClass(type) {
      const classes = {
        info: "blue lighten-5",
        success: "green lighten-5",
        warning: "orange lighten-5",
        error: "red lighten-5",
        billing: "purple lighten-5",
      };
      return classes[type] || "grey lighten-5";
    },

    formatTime(date) {
      return new Intl.DateTimeFormat("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }).format(date);
    },

    formatDateTime(dateStr) {
      if (!dateStr) return "N/A";
      return new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(dateStr));
    },

    getStatusText(status) {
      const statusMap = {
        0: "Pending",
        1: "Approved",
        2: "Completed",
        3: "Rejected",
      };
      return statusMap[status] || "Unknown";
    },
  },
};
</script>

<style scoped>
.events-log {
  font-size: 0.85em;
  line-height: 1.4;
}
</style>